import { defineStore } from "pinia";

import { useAgwManagerService } from "@/services/AgwManagerService";
import { useAppStore } from "@/store/AppStore";
const agwManagerService = useAgwManagerService();

export const useVideoIdentStore = defineStore("VideoIdentStore", {
  // Store the token to local store, so user is not
  // immediately thrown out if they refresh, come back later etc.
  state: () => {
    return {
      isLoading: false,
      contractSet: "",
      inputs: {
        forPers: {
          mailto: "",
          professions: [""],
          profTitles: [],
        },
        forTeam: {},
      },
      errors: {
        forPers: {
          professions: [""],
          profTitles: [""],
        },
        forTeam: {},
      },
      info: {
        forPers: {
          professions: [""],
          profTitles: [""],
        },
        forTeam: {},
      },

      additionalInvitations: {
        loading: true,
        errors: [],
        furtherEids: [],
        numEids: 0,
        buttonDisabled: false,
        emailSendProgress: "not started",
      },

      token: "",
      firstName: "",
      lastName: "",
      email: "",
      hinEmail: "",
      orgName: "",
      personalDataCompleted: Boolean,
      professionsWithTitle: {
        309343006: 1, // medical doctor
        46255001:  1, // pharmacist
        106289002: 1, // dentist
        59944000:  1, // psychologist
        3842006:   1, // chiro
      },
      type: "", // personal, team
      flatErrors: [],
      savingInProgress: false,
      savingErrors: [],
      videoIdentCompleted: false,
    };
  },
  actions: {
    // New flow: After submitting personal information, user gets to info page
    // where they see a table with additional invitations to be sent (if there are any).
    async fetchAdditionalInvitations() {
      // We support two cases.
      // - Token in the store, that is the
      //   case if user comes from productfinder wizard, step 6 (persons),
      //   or step 5 (teams).
      // - Token in query parameters. That happens if user comes here by clicking on
      //   invitation email link.
      if (!this.token) {
        const urlParams = new URLSearchParams(window.location.search);
        this.token = urlParams.get('token');
      }
      const responseData = await agwManagerService.fetchJson(
        "/video-ident/additional-invitations?token="+this.token,
        {
          method: "GET",
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
      const appStore = useAppStore();
      appStore.currentLanguage = responseData?.result?.language || 'DE';
      this.additionalInvitations.loading = false;
      if (responseData.errors) {
        // Do not show errors (unknown token) as long as old "eReg" wizard is active.
        // It will inevitably produce tokens that the AGW Manager does not know anything about.
        // Users of old wizard get here because we only have one set of callback pages
        // per environment.
        this.additionalInvitations.errors = responseData.errors;
        // appStore.header = "additionalInvitations.error.title";
        appStore.header = "additionalInvitations.withoutEids.title";
      }
      else if (responseData.result?.furtherEids){
        this.additionalInvitations.mailto = responseData.result.mailto;
        this.additionalInvitations.furtherEids = responseData.result.furtherEids;
        this.additionalInvitations.numEids = this.additionalInvitations.furtherEids.length
        if (this.additionalInvitations.numEids > 0) {
          appStore.header = "additionalInvitations.withEids.title";
          appStore.headerNum = this.additionalInvitations.numEids;
        }
        else {
          appStore.header = "additionalInvitations.withoutEids.title";
        }
      }
      else {
        appStore.header = "additionalInvitations.error.title";
      }
    },

    async sendFurtherInvitations() {
      // The ID-Cockpit does not re-send invitations, so it is
      // save to make this call unconditionally.
      // The code is simpler than remembering sending in the front end
      // and produce bugs (as it had happened).
      const responseData = await agwManagerService.fetchJson(
        "/video-ident/additional-invitations?token="+this.token,
        {
          method: "POST",
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
      if (responseData.result) {
        this.additionalInvitations.emailSendProgress = "success";
      }
      else {
        this.additionalInvitations.emailSendProgress = "failed";
      }
    },

    async handleSubmit() {
      this.savingInProgress = true;
      this.flatErrors.splice(0, this.flatErrors.length);
      const appStore = useAppStore();
      let type = "";
      if (appStore.isInvitedUser) {
        type = "person";
      } else {
        type = this.firstName ? "person" : "team";
      }
      // Make deep copy. Not very efficient. Should work here because only primitives.
      const payload = JSON.parse(JSON.stringify(
        this.firstName ? this.inputs.forPers : this.inputs.forTeam));
      payload.token = this.token;
      // profTitles can be `null` (so no field is shown). However, we can only
      // send empty strings.
      if (payload.profTitles) {
        payload.profTitles = payload.profTitles?.map(item => item ?? "");
      }
      // must not send empty professions
      if (payload.professions) {
        payload.professions = payload.professions.filter(item => item !== null && item !== "");
      }
      const responseData = await agwManagerService.fetchJson(
        "/video-ident/information-"+type,
        {
          method: "POST",
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload),
        }
      );
      if (responseData.errors) {
        _handleErrors(responseData, this.errors, this.flatErrors,
          this.savingErrors, this.firstName);
      }
      else {
        this.savingErrors.splice(0, this.savingErrors.length);
      }
      this.savingInProgress = false;
      return responseData;
    },
    // For "small wizard". Show error if token invalid and
    // get type for handling of personal vs team accounts.
    async validateToken() {
      const loaderTimeout = setTimeout(() => {
        this.isLoading = true;
      }, 1000);
      const responseData = await agwManagerService.fetchJson(
        "/video-ident/validate-token?token=" + this.token,
        {
          method: "GET",
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (responseData.errors) {
        this.token = null;
        if (responseData.errors[0]?.code === 9063) {
          this.videoIdentCompleted = true;
        }
        return false;
      }
      clearTimeout(loaderTimeout);
      this.isLoading = false;
      if (responseData.result) {
        // we're getting "type: [personal or team]"
        // whether the user is a personal or mpa eID
        this.type = responseData.result.type;
        const section = this.type === 'personal' ? 'forPers' : 'forTeam';
        if (responseData.result.type === "personal") {
          this.firstName             = responseData.result.firstName;
          this.lastName              = responseData.result.lastName;
          this.personalDataCompleted = responseData.result.personalDataCompleted;
          // Should user edit and the reload page, keep what they edited.
          if (!this.inputs[section].mailto) {
            this.inputs[section].mailto = responseData.result.mailto;
          }
        }
        return true;
        // It is an edge case that might happen in the future:
        // User does "big wizard", stops after screen 5.
        // Somebody, maybe support, send them a mail with link.
        // They get to the "small wizard".
      }
      else {
        this.token = null;
        return false;
      }
    }
  },
  persist: {
    storage: sessionStorage,
  }
});

function _handleErrors(responseData, errors, flatErrors, savingErrors, firstName) {
  flatErrors.push(responseData.errors);
  const camelCaseType = firstName ? "forPers" : "forTeam";
  for (const error of responseData.errors) {
    if (!error.path) {
      savingErrors.push(error);
      console.log(error);
      break;
    }

    let currentObject = errors[camelCaseType];

    // If user does not provide professions (which is an array with a
    // mandatory minimum length of 1), we get "/body/professions/: "Not enough elements: 0/1."
    // Set a "missing input" on the first element in that case makes sense
    // because that is what user has to do.
    if (error.path === "/body/professions" && error.message === "Not enough items: 0/1.") {
      currentObject.professions[0] = "Missing property.";
      continue;
    }

    const pathParts = error.path.split('/').slice(2); // Remove empty string at the beginning
    currentObject = _findCurrentObject(currentObject, pathParts);

    const elem = pathParts[pathParts.length -1];
    let itemMatch;
    if (elem && (itemMatch = elem.match(/^item(\d+)$/))) {
      currentObject[itemMatch[1]] = error.message;
    }
    else {
      currentObject[elem] = error.message;
    }
  }
}

function _findCurrentObject(currentObject, pathParts) {
  for (let i = 0; i < pathParts.length - 1; i++) {
    const key = pathParts[i];
    if (!currentObject[key]) {
      currentObject[key] = {};
    }
    currentObject = currentObject[key];
  }
  return currentObject;
}
